import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { ACCOUNT_TYPE_STYLES } from "../../../Common/TreeView";
import { ChartOfAccountStyles } from "./ChartOfAccountShared";

const emptyInputs = {
  parent_id: "",
  code: "",
  name: "",
  account_type: "",
  opening_amount: "",
  trial_balance_dr_cr: "",
  trial_opening_balance: "",
  description: "",
};
// The picker holds the whole active chart. On the edit form the account's own
// sub-tree comes back flagged isDisabled rather than missing, so the list still
// visibly contains every account.
const formatParentOption = (option) => (
  <div className="d-flex justify-content-between align-items-center">
    <span>{option.label}</span>
    <small className="text-muted ms-2">
      {option.disabled_reason || option.account_type || ""}
    </small>
  </div>
);

const Edit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [inputs, setInputs] = useState(emptyInputs);
  const [parents, setParents] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [accountRes, parentRes, typeRes] = await Promise.all([
          Http.get("/chart-of-account-code/" + id + "/edit"),
          // exclude={id} keeps the account and its own sub-tree out of the
          // parent list, so the branch cannot be re-parented into itself.
          Http.get("/chart-of-account-parent-options?exclude=" + id),
          Http.get("/chart-of-account-type-options"),
        ]);

        const account = accountRes.data;

        setInputs({
          parent_id: account.parent_id || "",
          code: account.code ?? "",
          name: account.name ?? "",
          account_type: account.account_type ?? "",
          opening_amount: account.opening_amount ?? "",
          trial_balance_dr_cr: account.trial_balance_dr_cr ?? "",
          trial_opening_balance: account.trial_opening_balance ?? "",
          description: account.description ?? "",
        });

        // Root accounts have no parent at all; the old code read
        // account.parent.name unconditionally and blanked the page.
        setSelectedParent(
          account.parent
            ? {
                label: account.parent.code + " - " + account.parent.name,
                value: account.parent.id,
              }
            : null
        );

        setParents(parentRes.data || []);
        setAccountTypes(typeRes.data || []);
      } catch (e) {
        console.error("Fetch error:", e);
        setError({
          load: ["Could not load this account. Please go back and try again."],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const parentHandleChange = (selectedOption) => {
    setSelectedParent(selectedOption);
    setInputs((values) => ({
      ...values,
      parent_id: selectedOption ? selectedOption.value : "",
    }));
  };

  const validate = () => {
    const errors = {};

    if (!String(inputs.code).trim()) {
      errors.code = ["Code is required."];
    }
    if (!String(inputs.name).trim()) {
      errors.name = ["Name is required."];
    }
    if (!inputs.account_type) {
      errors.account_type = ["Account type is required."];
    }

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const formSubmit = () => {
    if (saving || !validate()) {
      return;
    }

    setSaving(true);

    Http.put("/chart-of-account-code/" + id, {
      ...inputs,
      parent_id: inputs.parent_id || null,
      opening_amount: inputs.opening_amount === "" ? 0 : inputs.opening_amount,
    })
      .then((res) => {
        navigate("/chart-of-account", {
          state: {
            message: res.data.message,
            type: res.data.type || "success",
          },
        });
      })
      .catch((e) => {
        setError(
          e.response?.data?.errors || {
            general: [
              e.response?.data?.message ||
                "Could not update the account. Please try again.",
            ],
          }
        );
      })
      .finally(() => setSaving(false));
  };

  const typeStyle = ACCOUNT_TYPE_STYLES[inputs.account_type];

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Edit Chart of Account{" "}
              {inputs.code && <span className="coa-code">{inputs.code}</span>}
            </h5>
            <p className="text-muted mb-0 tx-12">
              {inputs.name || `Account #${id}`}
            </p>
          </div>
          <div className="d-flex gap-2">
            <Link
              to={`/chart-of-account-ledger/${id}`}
              className="btn btn-outline-secondary"
            >
              <i className="fa fa-list mg-r-6"></i>Ledger
            </Link>
            <Link to="/chart-of-account" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Back to List
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <div className="row">
            <div className="col-lg">
              <FormValidation error={error} />
            </div>
          </div>

          {loading ? (
            <div className="coa-panel">
              <div className="coa-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="coa-panel">
                <div className="coa-panel-head">
                  <span>
                    <i className="fa fa-sitemap me-2"></i>Account Identity
                  </span>
                  {typeStyle && (
                    <span
                      className="coa-pill"
                      style={{ background: typeStyle.bg, color: "#fff" }}
                    >
                      {typeStyle.label}
                    </span>
                  )}
                </div>
                <div className="coa-panel-body">
                  <div className="row">
                    <div className="col-lg-6">
                      <label>Parent Account</label>
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        placeholder="No parent (root account)"
                        name="parent_id"
                        options={parents}
                        value={selectedParent}
                        onChange={parentHandleChange}
                        formatOptionLabel={formatParentOption}
                        isOptionDisabled={(option) => option.isDisabled}
                        maxMenuHeight={320}
                        isClearable
                        isSearchable
                      />
                      <small className="coa-hint">
                        {parents.length} active accounts. Clear it to turn this
                        into a top-level account. This account and its own
                        sub-accounts are greyed out, since making one of them
                        the parent would detach the branch.
                      </small>
                    </div>
                    <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                      <label>
                        Code <i className="fa fa-star required"></i>
                      </label>
                      <input
                        type="text"
                        className="form-control coa-mono"
                        name="code"
                        value={inputs.code}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                      <label>
                        Name <i className="fa fa-star required"></i>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={inputs.name}
                        onChange={handleChange}
                        placeholder="Account name"
                      />
                    </div>
                  </div>

                  <div className="row mg-t-18">
                    <div className="col-lg-4">
                      <label>
                        Account Type <i className="fa fa-star required"></i>
                      </label>
                      <select
                        className="form-control"
                        name="account_type"
                        value={inputs.account_type}
                        onChange={handleChange}
                      >
                        <option value="">Select Account Type</option>
                        {accountTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <small className="coa-hint">
                        Which financial statement this account reports into.
                      </small>
                    </div>
                    <div className="col-lg-8 mg-t-10 mg-lg-t-0">
                      <label>Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        rows="1"
                        value={inputs.description}
                        onChange={handleChange}
                        placeholder="What this account is used for"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="coa-panel">
                <div className="coa-panel-head">
                  <span>
                    <i className="fa fa-balance-scale me-2"></i>Opening Balance
                  </span>
                  <span className="coa-head-note">
                    Only needed when carrying a balance forward
                  </span>
                </div>
                <div className="coa-panel-body">
                  <div className="row">
                    <div className="col-lg-4">
                      <label>Opening Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control coa-num"
                        name="opening_amount"
                        value={inputs.opening_amount}
                        onChange={handleChange}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                      <label>Trial Balance Type</label>
                      {/* Controlled via value= : the old `selected` attributes on
                          the options were ignored by React, so saved values never
                          showed up and a blind save wiped them. */}
                      <select
                        className="form-control"
                        name="trial_balance_dr_cr"
                        value={inputs.trial_balance_dr_cr}
                        onChange={handleChange}
                      >
                        <option value="">Select a Option</option>
                        <option value="dr">Dr — Debit</option>
                        <option value="cr">Cr — Credit</option>
                      </select>
                    </div>
                    <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                      <label>Show in Trial Opening Balance</label>
                      <select
                        className="form-control"
                        name="trial_opening_balance"
                        value={inputs.trial_opening_balance}
                        onChange={handleChange}
                      >
                        <option value="">Select a Option</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <Link
                  to="/chart-of-account"
                  className="btn btn-outline-secondary"
                >
                  Cancel
                </Link>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={formSubmit}
                  disabled={saving}
                >
                  <i
                    className={`fa ${
                      saving ? "fa-spinner fa-spin" : "fa-send"
                    } mg-r-10`}
                  ></i>
                  {saving ? "Updating..." : "Update Account"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Edit;
